import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ConnectError, Code } from '@connectrpc/connect';
import type { BladeDescriptor } from './types';

// Data seam for view-primitives (HUB-277). Primitives don't know where their data
// comes from — they call useBladeList / useBladeEntity, which resolve against the
// BladeDataSource provided by the shell. The routed shell (/w) provides a live
// @hub/api source; the backendless preview provides a static mock source. This is
// the seam through which PHI enters the UI, so: never log payloads, keep entity
// ids (not payloads) in query keys / URLs, and surface permission failures as a
// "denied" state rather than an error.

export interface BladeListRow {
  id: string;
  title: string;
  meta?: string;
}

export interface BladeListData {
  rows: BladeListRow[];
}

/** Sentinel the server substitutes for HIPAA identifier values when the caller
 * lacks phi:view-identifiers (HUB-357). Mirrors workqueue's RedactedTagValue.
 * The real value never crosses the wire — this marker only drives display. */
export const PHI_RESTRICTED_SENTINEL = '__PHI_RESTRICTED__';

export interface BladeField {
  label: string;
  value: string;
  mono?: boolean;
  /** value was redacted server-side (render a Restricted placeholder) */
  restricted?: boolean;
  /** canonical field name (HUB-408) — lets a ViewDefinition match this field by
   *  key to drive its order/label/render-hint. Absent for legacy fields (they
   *  fall back to the hardcoded layout). */
  key?: string;
}

export interface BladeRelated {
  id: string;
  meta?: string;
}

export interface BladeEntityData {
  title: string;
  eyebrow?: string;
  badge?: string;
  stats?: { label: string; value: string }[];
  fields: BladeField[];
  related?: BladeRelated[];
}

/** One global-search result: identity + relevance only (no content bodies).
 * `title` may arrive as the PHI sentinel for uncleared callers (HUB-357). */
export interface BladeSearchHit {
  id: string;
  /** entity type, e.g. "client" | "claim" | "code" */
  type: string;
  title: string;
}

export interface BladeSearchData {
  hits: BladeSearchHit[];
}

/** One field placement in a resolved view (HUB-408/HUB-277): the entity field
 *  name plus render hints. Layout metadata only — no PHI. */
export interface ViewDefinitionField {
  field: string;
  label?: string;
  /** render-hint idiom (the schema `as`), e.g. "mono" | "badge" */
  as?: string;
  /** render hint: "read" | "write" (authoritative access stays server-side) */
  access?: string;
}

export interface ViewDefinitionSection {
  id: string;
  label?: string;
  fields: ViewDefinitionField[];
}

/** The client-consumable shape of a server-side `view` definition (HUB-408).
 *  The record primitive maps it to field order/labels/grouping; the fuller
 *  HUB-277 binding renderer generalizes this across primitives. */
export interface ViewDefinition {
  id: string;
  label: string;
  primitive: string;
  entityType?: string;
  sections: ViewDefinitionSection[];
}

export interface BladeDataSource {
  /** rows for a list blade */
  fetchList(descriptor: BladeDescriptor): Promise<BladeListData>;
  /** detail for a bound entity blade */
  fetchEntity(descriptor: BladeDescriptor): Promise<BladeEntityData>;
  /** global-search hits for a search blade (`descriptor.params.q`) — HUB-281/HUB-369 */
  fetchSearch(descriptor: BladeDescriptor): Promise<BladeSearchData>;
  /** resolve the active view definition for a view key (HUB-408). Optional: a
   *  source that omits it — or returns null — makes primitives fall back to
   *  their hardcoded layout (zero-regression). */
  fetchViewDefinition?(viewKey: string): Promise<ViewDefinition | null>;
}

const BladeDataContext = createContext<BladeDataSource | null>(null);

export function BladeDataProvider({
  source,
  children,
}: {
  source: BladeDataSource;
  children: ReactNode;
}) {
  return <BladeDataContext.Provider value={source}>{children}</BladeDataContext.Provider>;
}

function useSource(): BladeDataSource {
  const s = useContext(BladeDataContext);
  if (!s) throw new Error('useBladeList/useBladeEntity require a BladeDataProvider');
  return s;
}

export type BladeQueryStatus = 'loading' | 'error' | 'denied' | 'success';

export interface BladeQuery<T> {
  status: BladeQueryStatus;
  data?: T;
}

/** Stable, PHI-free query key: the blade's navigable identity only. */
function keyFor(kind: string, d: BladeDescriptor): unknown[] {
  return ['blade', kind, d.key, d.entityId ?? null, d.params ?? null];
}

/** Map a thrown error to a blade status — permission failures become "denied". */
function statusForError(error: unknown): 'denied' | 'error' {
  if (error instanceof ConnectError) {
    if (error.code === Code.PermissionDenied || error.code === Code.Unauthenticated) {
      return 'denied';
    }
  }
  return 'error';
}

export function useBladeList(descriptor: BladeDescriptor): BladeQuery<BladeListData> {
  const source = useSource();
  const q = useQuery({
    queryKey: keyFor('list', descriptor),
    queryFn: () => source.fetchList(descriptor),
    staleTime: 10 * 1000,
  });
  if (q.isPending) return { status: 'loading' };
  if (q.isError) return { status: statusForError(q.error) };
  return { status: 'success', data: q.data };
}

/** Search binding (HUB-369): resolves `descriptor.params.q` against the source's
 * search fetcher. The query is fixed per blade instance (re-submitting from the
 * topbar opens a NEW ephemeral blade), so the key is stable for the blade's
 * lifetime. The query text lives only in this in-memory cache key — the blade is
 * ephemeral, so it never reaches the URL (F3), and keys are never logged. */
export function useBladeSearch(descriptor: BladeDescriptor): BladeQuery<BladeSearchData> {
  const source = useSource();
  const enabled = !!(descriptor.params?.q ?? '').trim();
  const q = useQuery({
    queryKey: keyFor('search', descriptor),
    queryFn: () => source.fetchSearch(descriptor),
    enabled,
    staleTime: 10 * 1000,
  });
  if (!enabled) return { status: 'success' }; // no query → primitive shows its prompt
  if (q.isPending) return { status: 'loading' };
  if (q.isError) return { status: statusForError(q.error) };
  return { status: 'success', data: q.data };
}

/** Resolve the active view definition for a view key (HUB-408). Best-effort and
 *  non-blocking: when the source can't serve a definition (no fetcher, no such
 *  view, or a NotFound), it resolves to success with no data so the primitive
 *  falls back to its hardcoded layout. Definitions are pure layout metadata
 *  (field names/labels/render hints) — no PHI — so they cache longer than
 *  entity data and the view key is safe in the query key. */
export function useViewDefinition(viewKey: string | undefined): BladeQuery<ViewDefinition> {
  const source = useSource();
  const enabled = !!viewKey && typeof source.fetchViewDefinition === 'function';
  const q = useQuery({
    queryKey: ['viewdef', viewKey ?? null],
    queryFn: () => source.fetchViewDefinition!(viewKey!),
    enabled,
    staleTime: 60 * 1000,
  });
  if (!enabled) return { status: 'success' };
  if (q.isPending) return { status: 'loading' };
  if (q.isError) return { status: statusForError(q.error) };
  return { status: 'success', data: q.data ?? undefined };
}

export function useBladeEntity(descriptor: BladeDescriptor): BladeQuery<BladeEntityData> {
  const source = useSource();
  const enabled = !!descriptor.entityId;
  const q = useQuery({
    queryKey: keyFor('entity', descriptor),
    queryFn: () => source.fetchEntity(descriptor),
    enabled,
    staleTime: 10 * 1000,
  });
  if (!enabled) return { status: 'success' }; // bindingless → primitive shows empty
  if (q.isPending) return { status: 'loading' };
  if (q.isError) return { status: statusForError(q.error) };
  return { status: 'success', data: q.data };
}
